import {useEffect, useState} from "react";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {useSelector} from "react-redux";
import moment from "moment";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import VLFCard from "@/components/Card";
import VLFDatePicker from "@/components/Input/DatePicker";
import VLFSelectNew from "@/components/Input/SelectNew";
import VLFTextField from "@/components/Input/TextField";

import {AthleticTestAPI} from "@/api/athletic_test";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";

import {
	dateFormat,
	movementPlayersTestTypes,
	testTypes,
	REQUIRED_FIELD,
	SUCCESSFUL_OPERATION,
	ERROR_OPERATION,
} from "@/utils/constants";
import {checkRequiredFields} from "@/utils/functions";
import {usePlayers} from "@/utils/hooks";

import "./index.scss";

const HandleAthleticTestPage = (props) => {
	const {edit, handleNotification} = props;

	const navigate = useNavigate();
	const {id} = useParams();

	const playerId = new URLSearchParams(useLocation().search).get("playerId");

	const clubId = useSelector((state) => state?.user?.clubId);
	const userId = useSelector((state) => state?.user?.userId);
	const userName = useSelector((state) => state?.user?.userName);

	const [loading, setLoading] = useState(false);
	const [errors, setErrors] = useState([]);
	const [fields, setFields] = useState({
		player: null,
		testType: null, // può essere object {value,label} o string
		testDate: null,
		details: {}, // valueType -> value
		description: "",
	});
	const [originalDetails, setOriginalDetails] = useState([]); // solo per UPDATE

	const {players} = usePlayers({
		setLoading,
		handleNotification: undefined,
		goalkeepers: false,
		prospect: false,
	});

	// --- Helpers di normalizzazione ---
	const testsList = fields?.player?.goalkeeper
		? testTypes
		: movementPlayersTestTypes;
	const testTypeValue =
		typeof fields?.testType === "object" && fields?.testType !== null
			? fields.testType.value
			: fields?.testType;
	const activeTest = testsList?.find((t) => t.value === testTypeValue);
	const orderedParams = (activeTest?.params || [])
		.slice()
		.sort((a, b) => parseInt(a.order || 0) - parseInt(b.order || 0));

	// --- API: GET by id (edit) ---
	const getTest = async () => {
		setLoading(true);
		try {
			const response = await AthleticTestAPI.getById(id);

			// Trasforma array details in oggetto valueType -> value
			const newDetails = {};
			(response.details || []).forEach((d) => {
				newDetails[d.valueType] = d.value;
			});

			setFields({
				...response,
				details: newDetails,
			});
			setOriginalDetails(response.details || []);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	// Pre-selezione del player quando si arriva con ?playerId in NEW
	useEffect(() => {
		if (!edit && players?.length && playerId) {
			const currentPlayer = players.find((p) => p.id === playerId);
			setFields((prev) => ({
				...prev,
				player: currentPlayer,
			}));
		}
	}, [edit, players, playerId]);

	// Carica i dati in EDIT
	useEffect(() => {
		if (edit && players?.length) {
			getTest();
		}
	}, [edit, players]);

	// Quando cambia testType, riallinea le chiavi di details ai parametri previsti dal test
	useEffect(() => {
		if (!testTypeValue) return;
		const nextActiveTest = testsList?.find((t) => t.value === testTypeValue);
		if (!nextActiveTest) return;

		setFields((prev) => {
			const nextDetails = {...(prev.details || {})};
			// Crea/ordina solo le chiavi previste dal test corrente
			const map = {};
			nextActiveTest.params?.forEach((p) => {
				map[p.value] = nextDetails[p.value] ?? "";
			});
			return {
				...prev,
				details: map,
			};
		});
	}, [testTypeValue]);

	const getRequiredFields = () => {
		return playerId
			? ["testDate", "testType"]
			: edit
				? ["testDate"]
				: ["testDate", "player", "testType"];
	};

	const saveTest = async () => {
		try {
			const errs = checkRequiredFields(getRequiredFields(), fields);
			setErrors(errs);

			if (!errs.length) {
				setLoading(true);

				const body = {
					...fields,
					playerId: playerId
						? playerId
						: edit
							? fields?.player?.id
							: fields?.player?.value,
					testDate: moment(fields.testDate).format("YYYY-MM-DDTHH:mm:ss"),
					testType: testTypeValue,
					details: Object.entries(fields?.details || {}).map(
						([valueType, value]) => {
							const id = originalDetails?.find(
								(od) => od.valueType === valueType,
							)?.id;
							return {id, valueType, value};
						},
					),
					clubId,
					userId,
					user: {userId, userName},
				};

				if (edit) {
					await AthleticTestAPI.delete(id);
				}

				const response = edit
					? await AthleticTestAPI.create(body)
					: await AthleticTestAPI.create(body);

				setTimeout(() => {
					navigate(`/athletic-tests/${response?.id || id}`);
					setLoading(false);
					handleNotification(SUCCESSFUL_OPERATION, "success");
				}, 1000);
			}
		} catch (error) {
			setLoading(false);
			handleNotification(ERROR_OPERATION, "error");
		}
	};

	const handleSubmit = (event) => {
		event.preventDefault();
		saveTest();
	};

	const goBack = () => navigate(-1);

	const mainActions = [
		{
			icon: <ArrowBackIcon />,
			title: "Indietro",
			onClick: goBack,
			color: "info",
		},
		{
			icon: <SaveIcon />,
			title: "Salva",
			onClick: handleSubmit,
			disabled: checkRequiredFields(getRequiredFields(), fields).length
				? true
				: false,
		},
	];

	const breadcrumbsElements = [
		{label: "Test atletici", path: "/athletic-tests"},
	];
	if (edit) {
		breadcrumbsElements.push(
			{label: "/"},
			{
				label: fields
					? `${moment(fields.testDate).format(dateFormat)} ${fields.player?.firstName || ""} ${fields.player?.lastName || ""}`
					: "",
				path: "/athletic-tests/" + id,
			},
		);
	}

	const fieldClassName = edit ? "col-sm-12 col-md-4" : "col-sm-12 col-md-6";

	return (
		<Page className="handle-athletic-test-container">
			<Loading visible={loading} />
			<Breadcrumbs
				elements={breadcrumbsElements}
				actions={<MainActions actions={mainActions} />}
			/>

			<div className="sheet-container">
				{/* Card: Info principali */}
				<div className="row no-margin">
					<div className="col-sm-12 padding-small">
						<VLFCard
							title="Informazioni principali"
							content={
								<div className="row">
									<div className={fieldClassName}>
										<VLFDatePicker
											label="Data"
											value={fields.testDate}
											onChange={(value) => {
												setErrors([]);
												setFields({...fields, testDate: value});
											}}
											required
											helperText={
												errors.indexOf("testDate") !== -1 ? REQUIRED_FIELD : ""
											}
											error={errors.indexOf("testDate") !== -1}
										/>
									</div>

									<div className={fieldClassName}>
										<VLFSelectNew
											options={players.map((p) => ({
												value: p.id,
												label: `${p.firstName} ${p.lastName}`,
												goalkeeper: p.goalkeeper,
											}))}
											value={
												edit || playerId
													? fields?.player
														? `${fields.player?.firstName} ${fields.player?.lastName}`
														: "---"
													: fields.player
											}
											onChange={(newValue) => {
												setErrors([]);
												setFields((prev) => ({
													...prev,
													player: newValue,
													testType: newValue ? prev.testType : null,
												}));
											}}
											label="Giocatore"
											required
											helperText={
												errors.indexOf("player") !== -1 ? REQUIRED_FIELD : ""
											}
											error={errors.indexOf("player") !== -1}
											editable={!edit && !playerId}
										/>
									</div>

									<div className={fieldClassName}>
										<VLFSelectNew
											options={testsList}
											value={fields.testType}
											onChange={(newValue) => {
												setErrors([]);
												const nextValue =
													typeof newValue === "object"
														? newValue?.value
														: newValue;
												const nextActiveTest = testsList?.find(
													(t) => t.value === nextValue,
												);
												const emptyDetails = {};
												nextActiveTest?.params?.forEach((p) => {
													emptyDetails[p.value] =
														fields?.details?.[p.value] ?? "";
												});
												setFields((prev) => ({
													...prev,
													testType: newValue,
													details: emptyDetails,
												}));
											}}
											label="Tipologia"
											required
											isDisabled={!playerId && !fields.player}
											helperText={
												errors.indexOf("testType") !== -1 ? REQUIRED_FIELD : ""
											}
											error={errors.indexOf("testType") !== -1}
											editable={!edit}
										/>
									</div>

									<div className="col-sm-12 col-md-6">
										<VLFTextField
											value={fields["description"] || ""}
											onChange={(e) => {
												setFields({...fields, description: e.target.value});
											}}
											label="Descrizione"
											size="small"
											multiline
											minRows={3}
											maxRows={3}
										/>
									</div>
								</div>
							}
							openable={false}
						/>
					</div>
				</div>

				{/* Card: Parametri */}
				<div className="row no-margin mt-2">
					<div className="col-sm-12 padding-small">
						<VLFCard
							title="Parametri"
							content={
								<>
									{activeTest ? (
										<div className="row">
											{orderedParams.map((param) => (
												<div
													key={param.value}
													className="col-sm-12 col-md-6 col-lg-4"
												>
													<VLFTextField
														value={fields?.details?.[param.value] ?? ""}
														onChange={(e) => {
															const val = e.target.value;
															setFields((prev) => ({
																...prev,
																details: {
																	...(prev.details || {}),
																	[param.value]: val,
																},
															}));
														}}
														label={param.label}
														size="small"
														type="number"
														end={param.um}
													/>
												</div>
											))}
										</div>
									) : (
										<div className="d-flex justify-content-center mt-2">
											Seleziona una tipologia di test
										</div>
									)}
								</>
							}
							openable={false}
						/>
					</div>
				</div>
			</div>
		</Page>
	);
};

export default HandleAthleticTestPage;
